import React, { useState, useMemo } from 'react';
import { useGetBadgeDashboardQuery } from '../../slices/badgeApi';
import BadgeCard from './BadgeCard';
import BadgeDetailsModal from './BadgeDetailsModal';
import './BadgeDashboard.css';

const BadgeDashboard = () => {
  const [filterType, setFilterType] = useState('all'); // all, earned, available
  const [sortBy, setSortBy] = useState('type'); // type, date, name
  const [selectedBadge, setSelectedBadge] = useState(null);
  const [linkedInShareData, setLinkedInShareData] = useState(null);

  const { 
    data: dashboardData, 
    isLoading, 
    error,
    refetch
  } = useGetBadgeDashboardQuery();

  // Simple data access from unified dashboard
  const badges = dashboardData?.badges || [];

  // Filter badges based on selected filter
  const filteredBadges = useMemo(() => {
    return badges.filter(badge => {
      switch (filterType) {
        case 'earned':
          return badge.is_earned;
        case 'available':
          return !badge.is_earned && badge.is_eligible;
        case 'locked':
          return !badge.is_earned && !badge.is_eligible;
        default:
          return true;
      }
    });
  }, [badges, filterType]);

  // Sort badges based on selected sort option
  const sortedBadges = useMemo(() => {
    return [...filteredBadges].sort((a, b) => {
      switch (sortBy) {
        case 'date':
          const dateA = a.earned_at || '0';
          const dateB = b.earned_at || '0';
          return new Date(dateB) - new Date(dateA);
        case 'name':
          return a.name.localeCompare(b.name);
        case 'type':
        default:
          const typeOrder = { 'fluency': 1, 'engagement': 2, 'impact': 3 };
          return (typeOrder[a.badge_type] || 4) - (typeOrder[b.badge_type] || 4);
      }
    });
  }, [filteredBadges, sortBy]);

  const handleViewDetails = (digitalBadge) => {
    setSelectedBadge(digitalBadge);
  };

  const handleDownloadJSON = (digitalBadge) => {
    // Could add analytics tracking here
    console.log('Badge JSON downloaded:', digitalBadge.credential_id);
  };

  const handleLinkedInShare = (shareData) => {
    setLinkedInShareData(shareData);
    
    // Open LinkedIn sharing in new window
    if (shareData.sharing_data?.direct_share_url) {
      window.open(shareData.sharing_data.direct_share_url, '_blank');
    }
  };

  const handleRefresh = () => {
    refetch();
  };

  if (isLoading) {
    return (
      <div className="badge-dashboard">
        <div className="badge-dashboard__loading">
          <div className="loading-spinner"></div>
          <p>Loading your badges...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="badge-dashboard">
        <div className="badge-dashboard__error">
          <h3>Unable to load badges</h3>
          <p>Please try refreshing the page or contact support if the problem persists.</p>
          <button className="badge-dashboard__retry" onClick={handleRefresh}>
            🔄 Retry
          </button>
        </div>
      </div>
    );
  }

  const earnedCount = badges.filter(b => b.is_earned).length;
  const availableCount = badges.filter(b => !b.is_earned && b.is_eligible).length;
  const totalCount = badges.length;

  return (
    <div className="badge-dashboard">
      <div className="badge-dashboard__header">
        <div className="badge-dashboard__title">
          <h1>Your Professional Badges</h1>
          <p>Track your teaching achievements and share them with your professional network</p>
        </div>
        
        <div className="badge-dashboard__summary">
          <div className="badge-summary-card">
            <span className="badge-summary-card__number">{earnedCount}</span>
            <span className="badge-summary-card__label">Earned</span>
          </div>
          <div className="badge-summary-card">
            <span className="badge-summary-card__number">{availableCount}</span>
            <span className="badge-summary-card__label">Available</span>
          </div>
          <div className="badge-summary-card">
            <span className="badge-summary-card__number">{totalCount}</span>
            <span className="badge-summary-card__label">Total</span>
          </div>
        </div>
      </div>

      <div className="badge-dashboard__controls">
        <div className="badge-dashboard__filters">
          <button 
            className={`filter-button ${filterType === 'all' ? 'filter-button--active' : ''}`}
            onClick={() => setFilterType('all')}
          >
            All Badges ({totalCount})
          </button>
          <button 
            className={`filter-button ${filterType === 'earned' ? 'filter-button--active' : ''}`}
            onClick={() => setFilterType('earned')}
          >
            Earned ({earnedCount})
          </button>
          <button 
            className={`filter-button ${filterType === 'available' ? 'filter-button--active' : ''}`}
            onClick={() => setFilterType('available')}
          >
            Available ({availableCount})
          </button>
          <button 
            className={`filter-button ${filterType === 'locked' ? 'filter-button--active' : ''}`}
            onClick={() => setFilterType('locked')}
          >
            Locked ({totalCount - earnedCount - availableCount})
          </button>
        </div>

        <div className="badge-dashboard__sorting">
          <label htmlFor="sort-select">Sort by:</label>
          <select 
            id="sort-select"
            value={sortBy} 
            onChange={(e) => setSortBy(e.target.value)}
            className="sort-select"
          >
            <option value="type">Badge Type</option>
            <option value="name">Name</option>
            <option value="date">Date Earned</option>
          </select>
        </div>

        <button 
          className="badge-dashboard__refresh"
          onClick={handleRefresh}
          title="Refresh badges"
        >
          🔄
        </button>
      </div>

      {sortedBadges.length === 0 ? (
        <div className="badge-dashboard__empty">
          <div className="badge-dashboard__empty-icon">🎖️</div>
          <h3>No badges match your current filter</h3>
          <p>Try adjusting your filter to see more badges.</p>
        </div>
      ) : (
        <div className="badge-dashboard__grid">
          {sortedBadges.map((badge) => (
            <BadgeCard
              key={badge.badge_id}
              badge={badge}
              onViewDetails={handleViewDetails}
              onLinkedInShare={handleLinkedInShare}
            />
          ))}
        </div>
      )}

      {/* Enhanced Badge Details Modal */}
      <BadgeDetailsModal
        badge={selectedBadge ? badges.find(b => b.badge_id === selectedBadge.badge_id) : null}
        digitalBadge={selectedBadge}
        isOpen={!!selectedBadge}
        onClose={() => setSelectedBadge(null)}
        onLinkedInShare={handleLinkedInShare}
        onDownloadJSON={handleDownloadJSON}
      />

      {/* LinkedIn Share Success Modal */}
      {linkedInShareData && (
        <div className="badge-modal-overlay" onClick={() => setLinkedInShareData(null)}>
          <div className="badge-modal" onClick={(e) => e.stopPropagation()}>
            <div className="badge-modal__header">
              <h3>LinkedIn Sharing</h3>
              <button 
                className="badge-modal__close"
                onClick={() => setLinkedInShareData(null)}
              >
                ×
              </button>
            </div>
            <div className="badge-modal__content">
              <p>Your badge sharing window should have opened in a new tab. If it didn't open automatically, you can use the manual sharing instructions below.</p>
              
              {linkedInShareData.sharing_data?.instructions && (
                <div className="badge-modal__instructions">
                  <h4>Manual Sharing Steps:</h4>
                  <ol>
                    {linkedInShareData.sharing_data.instructions.steps?.map((step, index) => (
                      <li key={index}>{step}</li>
                    ))}
                  </ol>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BadgeDashboard;